// Main application entry point
import { initAuth } from './auth.js';
import { loadMarkets, fetchBitcoinPrice } from './markets.js';
import { initUX, toast } from './ux-utils.js';

// Initialize View Transitions for smooth page navigation
if ('startViewTransition' in document) {
  console.log('View Transitions API supported');
  
  // Intercept all navigation links
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link) return;
    
    const href = link.getAttribute('href');
    // Only handle internal navigation
    if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return;
    
    // Check if it's same origin navigation
    try {
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) return;
      
      e.preventDefault();
      
      // Start view transition
      document.startViewTransition(() => {
        window.location.href = href;
      });
    } catch (err) {
      // If URL parsing fails, let default behavior happen
      console.error('Navigation error:', err);
    }
  }, { capture: true });
}

// Initialize app
document.addEventListener('DOMContentLoaded', () => {
  console.log('Coinlance Frontend Initialized');
  
  // Initialize UX features (scroll progress, animations, etc.)
  initUX();
  
  // Initialize authentication
  initAuth();
  
  // Initialize dot navigation
  initDotNavigation();
  
  // Load market data if on homepage
  if (document.getElementById('marketList')) {
    loadMarkets();
  }
  
  // Update hero BTC price if on homepage
  if (document.querySelector('.hero-btc-price')) {
    updateHeroBitcoinPrice();
    // Update every 30 seconds
    setInterval(updateHeroBitcoinPrice, 30000);
  }
  
  // Set up event listeners
  setupEventListeners();
});

// Update Bitcoin price in hero section
async function updateHeroBitcoinPrice() {
  const priceElement = document.querySelector('.hero-btc-price');
  const changeElement = document.querySelector('.hero-btc-change');
  
  if (!priceElement || !changeElement) return;
  
  const btcData = await fetchBitcoinPrice();
  
  if (btcData) {
    // Update price
    priceElement.textContent = '$' + btcData.price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    // Update change percentage
    const changeValue = btcData.change24h.toFixed(2);
    const changeSign = btcData.change24h >= 0 ? '+' : '';
    changeElement.textContent = changeSign + changeValue + '%';
    
    // Update badge class based on positive/negative
    changeElement.className = btcData.change24h >= 0 ? 'badge-success' : 'badge-error';
  }
}

function setupEventListeners() {
  // Login button
  const loginBtn = document.getElementById('loginBtn');
  if (loginBtn) {
    loginBtn.addEventListener('click', () => {
      window.location.href = '/login.html';
    });
  }
  
  // Register button
  const registerBtn = document.getElementById('registerBtn');
  if (registerBtn) {
    registerBtn.addEventListener('click', () => {
      window.location.href = '/signup.html';
    });
  }
  
  // Logout button
  const logoutBtn = document.getElementById('logoutBtn');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', handleLogout);
  }
}

function handleLogout() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userEmail');
  window.location.href = '/';
}

// Initialize dot navigation for mobile
function initDotNavigation() {
  const navDots = document.querySelectorAll('.nav-dot');
  if (navDots.length === 0) return;
  
  // Get current page from URL
  const currentPath = window.location.pathname;
  const currentPage = window.location.href.split('/').pop().split('?')[0] || 'index.html';
  
  console.log('Current href:', window.location.href);
  console.log('Current pathname:', currentPath);
  console.log('Current page:', currentPage);
  
  // Map current page to navigation path
  let normalizedPath;
  if (currentPage === '' || currentPage === 'index.html' || currentPath === '/') {
    normalizedPath = '/';
  } else if (currentPage === 'trading.html' || currentPath.includes('trading')) {
    normalizedPath = 'trading.html';
  } else if (currentPage === 'wallet.html' || currentPath.includes('wallet')) {
    normalizedPath = 'wallet.html';
  } else if (currentPage === 'profile.html' || currentPath.includes('profile')) {
    normalizedPath = 'profile.html';
  } else {
    normalizedPath = '/';
  }
  
  // Page order for direction detection (using relative paths)
  // Main navigation flow: Home -> Markets -> Wallet -> Profile
  const pageOrder = ['/', 'trading.html', 'wallet.html', 'profile.html'];
  const currentIndex = pageOrder.indexOf(normalizedPath);
  
  console.log('Normalized path:', normalizedPath);
  console.log('Current index:', currentIndex);
  console.log('Page order:', pageOrder);
  
  // Set active dot based on current page
  navDots.forEach((dot) => {
    const dotPage = dot.getAttribute('data-page');
    const isProtected = dot.getAttribute('data-protected') === 'true';
    
    // Check if this dot matches current page
    if (normalizedPath === dotPage) {
      dot.classList.add('active');
    } else {
      dot.classList.remove('active');
    }
    
    // Remove any existing click handlers to prevent duplicates
    const newDot = dot.cloneNode(true);
    dot.parentNode.replaceChild(newDot, dot);
    
    // Handler function for navigation
    const handleNavigation = function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      const targetIndex = pageOrder.indexOf(dotPage);
      console.log('Dot clicked:', dotPage, 'Target index:', targetIndex, 'Current index:', currentIndex);
      
      // Disable all dots temporarily to prevent double clicks
      document.querySelectorAll('.nav-dot').forEach(d => {
        d.style.pointerEvents = 'none';
        d.style.opacity = '0.5';
      });
      
      // Navigate to page
      navigateToPage(dotPage, targetIndex, currentIndex, isProtected);
      
      // Re-enable dots after a short delay (in case navigation is cancelled)
      setTimeout(() => {
        document.querySelectorAll('.nav-dot').forEach(d => {
          d.style.pointerEvents = '';
          d.style.opacity = '';
        });
      }, 500);
    };
    
    // Add both click and touchend handlers for better mobile support
    newDot.addEventListener('click', handleNavigation, { passive: false });
    newDot.addEventListener('touchend', handleNavigation, { passive: false });
  });
  
  // Add swipe gestures for mobile
  if (window.innerWidth < 768) {
    // Only enable swipe gestures if user is logged in (since protected pages require auth)
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (authToken) {
      initSwipeGestures(pageOrder, currentIndex);
    } else {
      console.log('Swipe gestures disabled - user not logged in');
    }
  }
  
  // Apply entry animation on page load
  const slideActive = localStorage.getItem('slideActive');
  const slideDirection = localStorage.getItem('slideDirection');
  
  if (slideActive === 'true' && slideDirection && window.innerWidth < 768) {
    localStorage.removeItem('slideActive');
    
    const mainContent = document.body;
    
    // Set starting position
    mainContent.style.transform = slideDirection === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
    mainContent.style.opacity = '0';
    
    // Animate in
    setTimeout(function() {
      mainContent.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s';
      mainContent.style.transform = 'translateX(0)';
      mainContent.style.opacity = '1';
      
      // Clean up
      setTimeout(function() {
        mainContent.style.transition = '';
        mainContent.style.transform = '';
        mainContent.style.opacity = '';
      }, 400);
    }, 50);
  }
}

// Navigate to a page with slide animation
function navigateToPage(targetPage, targetIndex, currentIndex, isProtected) {
  console.log('navigateToPage called:', { targetPage, targetIndex, currentIndex, isProtected });
  
  const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
  console.log('Auth token exists:', !!authToken);
  
  // Check if page requires authentication
  if (isProtected && !authToken) {
    console.log('Redirecting to login - protected page without auth');
    window.location.href = '/login.html';
    return;
  }
  
  // Don't navigate if already on this page
  if (targetIndex === currentIndex) {
    console.log('Already on target page, skipping navigation');
    // Re-enable dots since we're not navigating
    setTimeout(() => {
      document.querySelectorAll('.nav-dot').forEach(d => d.style.pointerEvents = '');
    }, 100);
    return;
  }
  
  // Check for invalid index - still navigate but without animation
  if (targetIndex === -1) {
    console.error('Invalid target index, navigating without animation:', targetPage);
    // Clear any animation flags
    localStorage.removeItem('slideDirection');
    localStorage.removeItem('slideActive');
    window.location.href = targetPage;
    return;
  }
  
  // Determine slide direction
  const slidingRight = targetIndex > currentIndex;
  console.log('Navigating with direction:', slidingRight ? 'right' : 'left');
  
  // Save transition state
  localStorage.setItem('slideDirection', slidingRight ? 'right' : 'left');
  localStorage.setItem('slideActive', 'true');
  
  // Navigate to page using proper path
  let navPath = targetPage;
  
  // Handle root path
  if (targetPage === '/' || targetPage === 'index.html') {
    navPath = '/';
  } 
  // Handle pages with leading slash - keep them as is for absolute paths
  else if (targetPage.startsWith('/')) {
    navPath = targetPage;
  }
  // Handle relative paths - add leading slash for proper navigation
  else if (!targetPage.startsWith('http')) {
    navPath = '/' + targetPage;
  }
  
  console.log('Navigating from current page to:', navPath);
  
  // Use View Transitions API if available
  if ('startViewTransition' in document) {
    document.startViewTransition(() => {
      window.location.href = navPath;
    });
  } else {
    window.location.href = navPath;
  }
}

// Initialize swipe gestures
function initSwipeGestures(pageOrder, currentIndex) {
  let touchStartX = 0;
  let touchEndX = 0;
  let touchStartY = 0;
  let touchEndY = 0;
  let isDragging = false;
  
  const minSwipeDistance = 50;
  const maxVerticalDistance = 100;
  
  console.log('Swipe gestures initialized for page index:', currentIndex);
  
  document.addEventListener('touchstart', function(e) {
    touchStartX = e.changedTouches[0].screenX;
    touchStartY = e.changedTouches[0].screenY;
    isDragging = true;
    console.log('Touch start at X:', touchStartX);
  }, { passive: true });
  
  document.addEventListener('touchmove', function(e) {
    if (!isDragging) return;
    
    touchEndX = e.changedTouches[0].screenX;
    touchEndY = e.changedTouches[0].screenY;
    
    const deltaX = touchEndX - touchStartX;
    const deltaY = Math.abs(touchEndY - touchStartY);
    
    // Only apply visual feedback if horizontal swipe is dominant
    if (Math.abs(deltaX) > 30 && deltaY < maxVerticalDistance) {
      const translateX = deltaX * 0.3; // Dampen the movement
      document.body.style.transform = `translateX(${translateX}px)`;
      document.body.style.transition = 'none';
    }
  }, { passive: true });
  
  document.addEventListener('touchend', function() {
    if (!isDragging) return;
    isDragging = false;
    
    // Reset transform
    document.body.style.transform = '';
    document.body.style.transition = '';
    
    const deltaX = touchEndX - touchStartX;
    const deltaY = Math.abs(touchEndY - touchStartY);
    
    console.log('Touch end - deltaX:', deltaX, 'deltaY:', deltaY);
    
    // Check if it's a horizontal swipe (not vertical scroll)
    if (deltaY > maxVerticalDistance) {
      console.log('Vertical scroll detected, ignoring');
      return;
    }
    
    // Swipe left (next page) - move forward
    if (deltaX < -minSwipeDistance) {
      if (currentIndex < pageOrder.length - 1) {
        console.log('Swiping LEFT to next page');
        const nextIndex = currentIndex + 1;
        const nextPage = pageOrder[nextIndex];
        const nextDot = document.querySelector(`.nav-dot[data-page="${nextPage}"]`);
        const isProtected = nextDot ? nextDot.getAttribute('data-protected') === 'true' : false;
        
        navigateToPage(nextPage, nextIndex, currentIndex, isProtected);
      } else {
        console.log('Already at last page, cannot swipe left further');
      }
    }
    // Swipe right (previous page) - move backward
    else if (deltaX > minSwipeDistance) {
      if (currentIndex > 0) {
        console.log('Swiping RIGHT to previous page');
        const prevIndex = currentIndex - 1;
        const prevPage = pageOrder[prevIndex];
        const prevDot = document.querySelector(`.nav-dot[data-page="${prevPage}"]`);
        const isProtected = prevDot ? prevDot.getAttribute('data-protected') === 'true' : false;
        
        navigateToPage(prevPage, prevIndex, currentIndex, isProtected);
      } else {
        console.log('Already at first page (Home), cannot swipe right further');
      }
    } else {
      console.log('Swipe distance too small');
    }
    
    touchStartX = 0;
    touchEndX = 0;
    touchStartY = 0;
    touchEndY = 0;
  }, { passive: true });
}

// Export for use in other modules
export { setupEventListeners };
